import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { AppState, AuthActionTypes, UserActionTypes } from '@app/core/ngrx';
import { Observable, of, Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { environment } from '@env/environment';
import { User } from '@app/core/models';
import { catchError, filter, flatMap, map, skipWhile } from 'rxjs/operators';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';
import { MSALAuthService } from '../msal-auth.service';

@Injectable({ providedIn: 'root' })
export class AuthProtectedGuard implements CanActivate, CanActivateChild {

  constructor(
    private router: Router,
    private store: Store<AppState>,
    private authService: MSALAuthService,
    private msalAuthService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) { }

  async validateUserAuthentication(state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return await new Promise(resolve => {
      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
        )
        .subscribe(res => {
          const account = this.msalAuthService.instance.getActiveAccount() || this.msalAuthService.instance.getAllAccounts()[0];
          if (account) {
            resolve(true);
          }
          else {
            // this is to handle the SSO from external app to frameboard
            const parts = state.url.split('#id_token=');
            if (parts.length > 0) {
              const idToken = parts[1];
              const decodedToken = jwtDecode(idToken) as any;
              if (decodedToken) {
                const ssoRequest = {
                  scopes: environment.MSAL.scopes,
                  loginHint: decodedToken.email
                };
                this.msalAuthService.ssoSilent(ssoRequest).subscribe(res => {
                  resolve(true);
                }, err => {
                  console.log('Error SSO request: ', err);
                  resolve(this.router.parseUrl('/'));
                })
              }
            } else {
              window.localStorage.setItem('returnUrl', state.url);
              resolve(this.router.parseUrl('/'));
            }
          }
        })
    })
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean {
    return this.validateUserAuthentication(state);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean {
    return this.validateUserAuthentication(state);
  }

}
